class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.installLoadingPlaceholder();

    // Runde 24: the upsell list is a horizontal scroller below 750 px. Shift+Tab
    // from the undo toast landed on a slide outside the viewport (x 954 at
    // 375 px). Bring the focused slide into view instead.
    if (!document.body.dataset.hfRecoFocus) {
      document.body.dataset.hfRecoFocus = 'true';
      document.addEventListener('focusin', (event) => {
        const slide = event.target.closest && event.target.closest('.cart-recommendations .mini-cart__navigation > li');
        if (slide) slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);

    // Whichever path opens the drawer (add to cart, quick add, cart:refresh,
    // header icon), move focus to the drawer title and hand it back on close.
    // Only an opener already inside the panel (not the summary) is left alone.
    const details = this.drawer && this.drawer.querySelector('details');
    if (details) {
      new MutationObserver(() => {
        if (!details.hasAttribute('open')) return;
        // Runde 33: the loading text goes up the moment the drawer opens, not
        // only when the IntersectionObserver fires during the slide-in.
        if (!this.querySelector('form.mini-cart')) this.setLoading(true);
        const opener = document.activeElement;
        if (details.contains(opener) && !(opener.closest && opener.closest('summary'))) return;
        this.returnFocusTo = opener;
        this.focusTitle();
      }).observe(details, { attributes: true, attributeFilter: ['open'] });

      this.drawer.addEventListener('close', () => {
        const target = this.returnFocusTo;
        this.returnFocusTo = null;
        // The theme leaves aria-expanded="true" on the header icon after Escape (Runde 16).
        const expandedSummary = details.querySelector('summary[aria-expanded]');
        if (expandedSummary) expandedSummary.setAttribute('aria-expanded', 'false');
        if (!target || !target.focus) return;
        // The theme moves focus to the header icon once the closing transition
        // ends, which would override an immediate restore (Runde 15): keep
        // restoring for a moment, but stop as soon as the customer moved on.
        const summary = details.querySelector('summary');
        const restore = (attempt) => {
          if (!document.contains(target)) return;
          const active = document.activeElement;
          const parked = active === document.body || active === summary || details.contains(active);
          if (active !== target && parked) target.focus({ preventScroll: true });
          if (attempt < 12 && (parked || active === target)) window.setTimeout(() => restore(attempt + 1), 100);
        };
        restore(0);
      });
    }
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    this.setLoading(true);
    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
        this.setLoading(false);

          // This first render replaces the drawer's content; if the drawer is
          // already open and held focus on its title, put focus back there.
          if (this.drawer && this.drawer.querySelector('details[open]') && document.activeElement === document.body) {
            this.focusTitle();
          }
          this.applyPendingLineHint();
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        this.setLoading(false, true);
        console.error(e);
      });
  }

  // Runde 33: Until the first section response arrives the drawer showed only
  // the spinner from header-body.liquid (2–3 s without a word, Lauf 20). The
  // text is added here because header-body.liquid and de.json belong to other
  // tasks; theme.cartStrings.loading takes over once that key exists.
  installLoadingPlaceholder() {
    if (this.querySelector('form.mini-cart') || this.querySelector('.mini-cart__loading')) return;
    const overlay = this.querySelector(':scope > .loading-overlay');
    if (!overlay) return;
    const box = document.createElement('div');
    box.className = 'mini-cart__loading';
    const text = document.createElement('p');
    text.className = 'mini-cart__loading-text';
    text.setAttribute('role', 'status');
    text.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-hidden', 'true');
    box.appendChild(overlay);
    box.appendChild(text);
    this.appendChild(box);
  }

  // The text is written when loading starts (drawer opening), not on page
  // load, so the live region announces it exactly once, when it matters.
  setLoading(loading, failed) {
    const text = this.querySelector('.mini-cart__loading-text');
    const strings = (window.theme && theme.cartStrings) || {};
    if (loading) {
      this.setAttribute('aria-busy', 'true');
      if (text) text.textContent = strings.loading || 'Warenkorb wird geladen …';
      return;
    }
    this.removeAttribute('aria-busy');
    if (failed && text) {
      const spinner = this.querySelector('.mini-cart__loading > .loading-overlay');
      if (spinner) spinner.hidden = true;
      text.textContent = strings.error || 'Dein Warenkorb konnte nicht geladen werden. Bitte versuch es noch einmal.';
    }
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }

    // Opened by script (add to cart, quick add), not by the header icon:
    // MenuDrawer sets no focus trap in that case. The open-attribute observer
    // in connectedCallback moves focus to the drawer title and returns it on close.
    this.drawer.openMenuDrawer();
  }

  // The panel becomes focusable only once its visibility transition has
  // started, so retry for up to 3 s until focus actually lands on the title.
  focusTitle(attempt = 0) {
    const detailsElement = this.drawer && this.drawer.querySelector('details');
    if (!detailsElement || !detailsElement.hasAttribute('open')) return;
    const title = this.querySelector('#mini-cart-title');
    if (title) {
      if (document.activeElement === title) return; // Runde 21: nothing to do, no re-focus
      if (typeof trapFocus === 'function') {
        trapFocus(detailsElement, title);
      } else {
        title.focus({ preventScroll: true });
      }
      if (document.activeElement === title) return;
    }
    if (attempt < 30) window.setTimeout(() => this.focusTitle(attempt + 1), 100);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // While the drawer is open (upsell adds, gift wrap, quick view),
    // patch the money-touched regions instead of swapping the whole
    // drawer — keeps open collapsibles, scroll and upsell state intact.
    const drawerOpen = this.drawer && !!this.drawer.querySelector('details[open]');

    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) return;

      if (
        section.id === 'mini-cart' &&
        drawerOpen &&
        window.theme && theme.patchMiniCart &&
        parsedState.sections[section.id] &&
        theme.patchMiniCart(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector))
      ) return;

      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    this.open();
    // Runde 25/27: quick-add, product form and undo render through here; keep
    // every line at the limit and bring the added line into view.
    this.enforceAfterRender(parsedState);
    this.pendingScrollKey = (parsedState && parsedState.key) || null;
    window.setTimeout(() => { this.pendingScrollKey = null; }, 4000);
    this.applyPendingLineHint();
    [120, 800].forEach((delay) => window.setTimeout(() => this.applyPendingLineHint(), delay));
    this.announceAdded(parsedState);
  }

  // Runde 28: nach dem Hinzufügen blieb die Live-Region des Drawers leer, der
  // Screenreader hörte nur „Dein Warenkorb“. Jetzt: „Balance Trio: In den
  // Warenkorb gelegt“. Nur für Antworten von add.js (eine Position oder items
  // ohne item_count), nicht für ganze Warenkorb-Stände. Geschrieben wird nach dem
  // Rendern; ersetzt ein späteres Rendern die Region, wird einmal nachgezogen.
  announceAdded(parsedState) {
    const message = window.theme && theme.cartStrings && theme.cartStrings.itemAdded;
    if (!message || !parsedState || parsedState.item_count !== undefined) return;
    const lines = Array.isArray(parsedState.items) ? parsedState.items : (parsedState.variant_id ? [parsedState] : []);
    const names = lines
      .map((line) => String(line.product_title || line.title || '').replace(/^Hashifit\s+/i, '').split(' · ')[0].trim())
      .filter(Boolean);
    if (!names.length) return;
    const text = `${Array.from(new Set(names)).join(', ')}: ${message}`;
    const write = () => {
      const region = document.getElementById('CartDrawer-LiveRegionText');
      if (!region || region.dataset.announced === text) return;
      region.textContent = text;
      region.dataset.announced = text;
      region.setAttribute('aria-hidden', 'false');
    };
    [350, 1300].forEach((delay) => window.setTimeout(write, delay));
  }

  // Runde 27: add.js answers with the line, not the cart, so enforceLineMax saw
  // no items on the product-form path (10 + 1 = 11 slipped through); load the
  // cart state when the response has none.
  enforceAfterRender(parsedState) {
    const run = (cart) => {
      const cartItems = document.querySelector('#mini-cart cart-items');
      if (cartItems && cartItems.enforceLineMax) cartItems.enforceLineMax(cart);
    };
    if (parsedState && Array.isArray(parsedState.items)) {
      run(parsedState);
      return;
    }
    fetch(`${theme.routes.cart_url || '/cart'}.js`)
      .then((response) => response.json())
      .then(run)
      .catch(() => {});
  }

  // Runde 25: a quick-add at the limit opens the drawer with the hint on that
  // line. The first open re-renders the drawer (handleIntersection), so the
  // hint is kept here and written after whichever render comes last.
  applyPendingLineHint() {
    // Runde 27: the freshly added line scrolls into view (A: the Trio sat
    // below the fold after adding).
    const key = this.pendingScrollKey;
    if (key) {
      const field = document.querySelector(`#mini-cart [data-key="${key}"]`);
      const row = field && field.closest('li');
      if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    }
    const pending = this.pendingLineHint;
    if (!pending) return;
    const cartItems = document.querySelector('#mini-cart cart-items');
    if (cartItems && cartItems.showLineMaxHint) cartItems.showLineMaxHint(pending.line, pending.max);
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);

// --- Keyboard access for custom-element controls (Impeccable, 2026-10-20) ---
// <add-to-cart> (quick add on product cards) and <drawer-close-button> carry
// role="button" tabindex="0" in the markup; Enter and Space must act like a
// click. Delegated on the document so re-rendered drawer content stays covered.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
  const control = event.target && event.target.closest ? event.target.closest('add-to-cart, drawer-close-button') : null;
  if (!control || event.target !== control) return;
  event.preventDefault();
  control.click();
});

// Escape must close the drawer even when focus fell out of it (e.g. after a
// removed control): the theme's handler listens on the drawer itself (Runde 14).
document.addEventListener('keyup', (event) => {
  const code = event.code ? event.code.toUpperCase() : String(event.key || '').toUpperCase();
  if (code !== 'ESCAPE') return;
  const drawer = document.querySelector('cart-drawer');
  const details = drawer && drawer.querySelector('details[open]');
  if (!details || drawer.contains(document.activeElement)) return;
  if (typeof drawer.closeMenuDrawer === 'function') {
    drawer.closeMenuDrawer(event, details.querySelector('summary'));
  }
});

// Runde 28: Gutschein-, Notiz- und Versand-Panel im Drawer. Beim Öffnen blieb der
// Fokus auf der Zeile „Gutschein“, die das Panel verdeckt; jetzt landet er im
// ersten Eingabefeld. Beim Schließen geht er zurück auf die Zeile, falls er im
// Panel lag (sonst stand er auf <body>).
document.addEventListener('toggle', (event) => {
  const panel = event.target;
  if (!(panel instanceof HTMLDetailsElement) || !panel.classList.contains('mini-cart__action')) return;
  const summary = panel.querySelector('summary');
  if (panel.open) {
    const field = panel.querySelector('input:not([type="hidden"]), textarea, select');
    if (field) window.setTimeout(() => { if (panel.open) field.focus({ preventScroll: true }); }, 60);
    return;
  }
  const active = document.activeElement;
  if (summary && (!active || active === document.body || panel.contains(active))) {
    summary.focus({ preventScroll: true });
  }
}, true);

// The theme's quantity stepper only toggles a visual "disabled" class at the
// min/max; mirror it as aria-disabled so assistive tech announces the state.
(() => {
  const sync = (root) => root.querySelectorAll('.quantity__button').forEach((button) => {
    button.setAttribute('aria-disabled', button.classList.contains('disabled') ? 'true' : 'false');
  });
  customElements.whenDefined('quantity-input').then(() => {
    const proto = customElements.get('quantity-input').prototype;
    const original = proto.validateQtyRules;
    if (typeof original === 'function' && !original.__ariaPatched) {
      const patched = function () { original.apply(this, arguments); sync(this); };
      patched.__ariaPatched = true;
      proto.validateQtyRules = patched;
    }
    sync(document);
  });
})();
